/**
 * The read surfaces the outcome cards imitate: X, Reddit, Google, Google Maps,
 * Instagram and a short-form feed, plus the drawn avatars, storefronts and video
 * stills that stand in for the photographs those surfaces would carry.
 *
 * Companion to `outcomeUi.css.ts`, which owns the two compose windows. Split
 * because they are two jobs: that file is a draft waiting to be sent, this one
 * is a page the run went and read. Same rules apply to both.
 *
 * These are deliberately NOT in `tokens.css` and deliberately do NOT respond to
 * `data-theme`. Everything else on the page is ours and follows the reader's
 * theme; these cards stand in for somebody else's product, and an X post that
 * turns dark because our site did is an X post nobody recognises. They are
 * pinned light for the same reason a screenshot would be.
 *
 * The values are each platform's own long-standing interface colours: X's
 * #0f1419 on #eff3f4 dividers with #536471 secondary text and the #1d9bf0 check,
 * Reddit's #d93a00 upvote and #eaedef action pills, Google's #1a0dab result link
 * over #4d5156 snippets, Instagram's #0095f6 and its #efefef controls. Check
 * them against the live products before changing one; the numbers are only worth
 * anything as a record of what those interfaces show.
 *
 * The font is the platform UI stack and not our Inter, for the reason given in
 * `outcomeUi.css.ts`. (No backticks in this file: it is one template literal.)
 */
export const PLATFORM_UI_CSS = `
.pu{font-family:ui-sans-serif,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
border-radius:10px;overflow:hidden;background:#fff;border:1px solid #e0dfdc;
box-shadow:0 1px 2px rgba(10,15,34,.06);}
.pu-hair{border-top:1px solid #eceff1;}
/* Drawn profile pictures. Deliberately muted rather than pastel - these stand
   in for photographs, and the brand rule against pastels is about our surface,
   which is what a bright avatar would start to look like. Four skin tones, four
   hair colours, five backgrounds and a pair of glasses give enough distinct
   people for a search-results list not to read as one person repeated. */
.pu-av-skin0{fill:#f0c9a4;}.pu-av-skin1{fill:#d9a173;}
.pu-av-skin2{fill:#a3714a;}.pu-av-skin3{fill:#6b4630;}
.pu-av-hair0{fill:#2f2a26;}.pu-av-hair1{fill:#5c4033;}
.pu-av-hair2{fill:#8a6a4b;}.pu-av-hair3{fill:#c8a06a;}
.pu-av-bg0{fill:#cfd8dc;}.pu-av-bg1{fill:#d7cfc4;}.pu-av-bg2{fill:#c7d3cb;}
.pu-av-bg3{fill:#d3ccd8;}.pu-av-bg4{fill:#d8cec7;}
.pu-av-top0{fill:#5b6b73;}.pu-av-top1{fill:#7a6a58;}.pu-av-top2{fill:#5c6f63;}
.pu-av-top3{fill:#6a6072;}.pu-av-top4{fill:#7b6a63;}
.pu-av-glass{stroke:#3a3532;}
/* Company avatar: a logo on a tile, in a colour a brand would pick. Not our
   blue - the whole panel is built on blue meaning AnyAPI. */
.pu-brand-tile{fill:#1c2b4a;}
.pu-brand-mark{stroke:#7fd1c4;}
/* Drawn storefronts, for the Maps result thumbnails. */
.pu-pl-sky0{fill:#dde5ec;}.pu-pl-sky1{fill:#e3e2da;}.pu-pl-sky2{fill:#dce6e0;}.pu-pl-sky3{fill:#e6dfe2;}
.pu-pl-wall0{fill:#b9c4ce;}.pu-pl-wall1{fill:#c8c2b2;}.pu-pl-wall2{fill:#b6c6bd;}.pu-pl-wall3{fill:#c7bcc1;}
.pu-pl-awn0{fill:#4a6b8a;}.pu-pl-awn1{fill:#8a6a3f;}.pu-pl-awn2{fill:#4f7a63;}.pu-pl-awn3{fill:#7a5568;}
.pu-pl-stripe{fill:#f4f2ec;}
.pu-pl-glass{fill:#8fa6b8;}
.pu-pl-door{fill:#6f7d88;}
/* Google Maps: the local pack. Its rating star and review count are the part
   everyone reads first, so they get the platform's own amber. */
.pu-mp-name{color:#202124;font-weight:500;}
.pu-mp-sub{color:#70757a;}
.pu-mp-star{color:#e7711b;}
.pu-mp-chip{background:#e6f4ea;color:#137333;border-radius:999px;}
.pu-mp-head{background:#f8f9fa;border-bottom:1px solid #e5e7eb;}
/* X */
.pu-x-name{color:#0f1419;font-weight:700;}
.pu-x-sub{color:#536471;}
.pu-x-body{color:#0f1419;}
.pu-x-act{color:#536471;}
.pu-x-check{color:#1d9bf0;}
.pu-x-tick{stroke:#fff;}
.pu-x-thread{background:#cfd9de;width:2px;border-radius:2px;}
/* Reddit, including Snoo. The body stays white whatever the account picked,
   because that is Snoo; the block behind it and the shirt are what vary. */
.pu-sn-body{fill:#fff;}
.pu-sn-eye{fill:#1c1c1c;}
.pu-sn-mark{fill:#ff4500;}
.pu-sn-antenna{stroke:#ff4500;fill:none;}
.pu-sn-smile{stroke:#1c1c1c;fill:none;}
.pu-sn-bg0{fill:#d93a00;}.pu-sn-bg1{fill:#0079d3;}.pu-sn-bg2{fill:#46a508;}
.pu-sn-bg3{fill:#7193ff;}.pu-sn-bg4{fill:#ffb000;}
.pu-sn-shirt0{fill:#24a0ed;}.pu-sn-shirt1{fill:#ea0027;}.pu-sn-shirt2{fill:#0dd3bb;}
.pu-sn-shirt3{fill:#ff8717;}.pu-sn-shirt4{fill:#7e53c1;}
.pu-rd-title{color:#0f1a1c;font-weight:600;}
.pu-rd-sub{color:#576f76;}
.pu-rd-body{color:#1c1c1c;}
.pu-rd-pill{background:#eaedef;color:#0f1a1c;border-radius:999px;}
.pu-rd-up{color:#d93a00;}
/* Google */
.pu-g-box{border:1px solid #dfe1e5;border-radius:999px;color:#202124;}
.pu-g-site{color:#202124;}
.pu-g-path{color:#4d5156;}
.pu-g-title{color:#1a0dab;}
.pu-g-snip{color:#4d5156;}
.pu-g-date{color:#70757a;}
.pu-g-fav{background:#f1f3f4;color:#5f6368;border-radius:999px;font-weight:600;}
.pu-g-mag{color:#4285f4;}
/* Short-form feed. The tile is TikTok's near-black, which is the background all
   three of these feeds put a video on, so one fill serves the row. */
.pu-clip{background:#161823;color:#fff;border-radius:8px;}
.pu-clip-hook{color:#fff;font-weight:700;}
.pu-clip-sub{color:#575b66;}
/* The scrim is what keeps the hook legible over whatever the still is doing
   underneath it, and it is also what every one of these feeds puts there. */
.pu-clip-scrim{background:linear-gradient(to top,rgba(0,0,0,.72),rgba(0,0,0,.45) 55%,transparent);}
/* Muted grading and broad light fields keep the object scenes closer to phone
   footage than to diagrams. Each palette preserves the same value structure. */
.pu-cv-palette0{--cv-bg-a:#d8c4a8;--cv-bg-b:#8e6e5b;--cv-light-a:#fff4cf;--cv-light-b:#e8c38d;--cv-shadow:#392f2b;--cv-surface:#9d7761;--cv-surface-hi:#dfc6a5;--cv-object:#4b5960;--cv-object-hi:#8da0a3;--cv-paper:#eee4d1;--cv-paper-soft:#d9cdb9;--cv-ink:#6f6558;--cv-accent:#ad684b;--cv-metal:#c6b79c;--cv-glass:#b4ced0;--cv-liquid:#5d3b2b;--cv-fabric:#58665d;--cv-hair:#342e2b;}
.pu-cv-palette1{--cv-bg-a:#bdc9c0;--cv-bg-b:#687e79;--cv-light-a:#f3edc9;--cv-light-b:#b9d8cc;--cv-shadow:#263432;--cv-surface:#718a82;--cv-surface-hi:#d2d7c7;--cv-object:#3f5357;--cv-object-hi:#81979a;--cv-paper:#e8e6d8;--cv-paper-soft:#cfd5c9;--cv-ink:#5c6862;--cv-accent:#b36f50;--cv-metal:#b6b8aa;--cv-glass:#bad6d2;--cv-liquid:#604337;--cv-fabric:#59675f;--cv-hair:#2e302f;}
.pu-cv-palette2{--cv-bg-a:#d2b9ad;--cv-bg-b:#8f6b66;--cv-light-a:#f7e2bd;--cv-light-b:#d7a999;--cv-shadow:#3d2c2c;--cv-surface:#8d6961;--cv-surface-hi:#dec6b4;--cv-object:#554a4d;--cv-object-hi:#907d7c;--cv-paper:#efe2cf;--cv-paper-soft:#d9c8b7;--cv-ink:#715f5a;--cv-accent:#9a5d48;--cv-metal:#bcae9d;--cv-glass:#c9d0c7;--cv-liquid:#5d382c;--cv-fabric:#63595b;--cv-hair:#33292a;}
.pu-cv-palette3{--cv-bg-a:#c7c5aa;--cv-bg-b:#74775b;--cv-light-a:#f2eac2;--cv-light-b:#c9cc9f;--cv-shadow:#303226;--cv-surface:#797b5f;--cv-surface-hi:#d5d0ad;--cv-object:#4b5149;--cv-object-hi:#899084;--cv-paper:#e9e5cf;--cv-paper-soft:#d0cfb6;--cv-ink:#626458;--cv-accent:#a76a48;--cv-metal:#b8b6a0;--cv-glass:#b8d0c8;--cv-liquid:#573d2f;--cv-fabric:#555f52;--cv-hair:#2e2e29;}
.pu-cv-palette4{--cv-bg-a:#c8b8bf;--cv-bg-b:#796a75;--cv-light-a:#f3dfc5;--cv-light-b:#d2b6bf;--cv-shadow:#332d34;--cv-surface:#7d6974;--cv-surface-hi:#d9c6c5;--cv-object:#504b57;--cv-object-hi:#8e8794;--cv-paper:#eee1d5;--cv-paper-soft:#d6c7c6;--cv-ink:#6b5f68;--cv-accent:#a86754;--cv-metal:#bcb0ad;--cv-glass:#becfd0;--cv-liquid:#59372f;--cv-fabric:#5e5964;--cv-hair:#312c31;}
.pu-cv-bg-a{stop-color:var(--cv-bg-a);}.pu-cv-bg-b{stop-color:var(--cv-bg-b);}
.pu-cv-light-a{stop-color:var(--cv-light-a);stop-opacity:.92;}.pu-cv-light-b{stop-color:var(--cv-light-b);stop-opacity:0;}
.pu-cv-shade-a{stop-color:var(--cv-shadow);stop-opacity:0;}.pu-cv-shade-b{stop-color:var(--cv-shadow);stop-opacity:.34;}
.pu-cv-scene{fill:var(--pu-cv-scene-fill);}.pu-cv-vignette{fill:var(--pu-cv-shade-fill);}
.pu-cv-light{fill:var(--pu-cv-light-fill);opacity:.8;}.pu-cv-light-bar{fill:var(--cv-light-a);opacity:.19;}
.pu-cv-object-shadow{fill:var(--cv-shadow);opacity:.24;}.pu-cv-desk{fill:var(--cv-surface);}
.pu-cv-paper{fill:var(--cv-paper);}.pu-cv-paper-soft{fill:var(--cv-paper-soft);}
.pu-cv-page-line,.pu-cv-note-line{fill:none;stroke:var(--cv-ink);stroke-linecap:round;opacity:.46;}
.pu-cv-page-line{stroke-width:1.4;}.pu-cv-note-line{stroke-width:.7;}
.pu-cv-tracker-open{fill:var(--cv-paper);stroke:var(--cv-ink);stroke-width:.7;}
.pu-cv-tracker-filled{fill:var(--cv-accent);}.pu-cv-pen{fill:var(--cv-object);}
.pu-cv-metal{fill:var(--cv-metal);}.pu-cv-pen-line{fill:none;stroke:var(--cv-object-hi);stroke-width:1;}
.pu-cv-cup{fill:var(--cv-surface-hi);}.pu-cv-cup-handle{fill:none;stroke:var(--cv-surface-hi);stroke-width:5;}
.pu-cv-cup-rim{fill:var(--cv-paper);}.pu-cv-liquid{fill:var(--cv-liquid);}
.pu-cv-phone{fill:var(--cv-object);}.pu-cv-phone-detail{fill:var(--cv-object-hi);}
.pu-cv-phone-line{fill:none;stroke:var(--cv-object-hi);stroke-width:1;stroke-linecap:round;}
.pu-cv-window{fill:var(--cv-glass);}.pu-cv-window-frame{fill:none;stroke:var(--cv-paper);stroke-width:2;opacity:.62;}
.pu-cv-laptop{fill:var(--cv-object);}.pu-cv-laptop-screen{fill:var(--cv-glass);}
.pu-cv-laptop-glare{fill:var(--cv-light-a);opacity:.3;}.pu-cv-laptop-base{fill:var(--cv-metal);}
.pu-cv-laptop-line{fill:none;stroke:var(--cv-object);stroke-width:1.2;stroke-linecap:round;}
.pu-cv-hair{fill:var(--cv-hair);}.pu-cv-hair-shine{fill:none;stroke:var(--cv-object-hi);stroke-width:3;stroke-linecap:round;opacity:.32;}
.pu-cv-fabric{fill:var(--cv-fabric);}.pu-cv-fabric-shade{fill:var(--cv-shadow);opacity:.35;}
.pu-cv-blind{fill:var(--cv-object);opacity:.28;}.pu-cv-clock-handle{fill:none;stroke:var(--cv-object);stroke-width:3;stroke-linecap:round;}
.pu-cv-clock-bell,.pu-cv-clock-body{fill:var(--cv-object);}.pu-cv-clock-face{fill:var(--cv-paper);}
.pu-cv-clock-feet{fill:none;stroke:var(--cv-object);stroke-width:4;stroke-linecap:round;}
.pu-cv-clock-marks{fill:none;stroke:var(--cv-ink);stroke-width:1.4;stroke-linecap:round;}
.pu-cv-clock-hands{fill:none;stroke:var(--cv-ink);stroke-width:2.3;stroke-linecap:round;stroke-linejoin:round;}
.pu-cv-clock-pin{fill:var(--cv-accent);}
/* Instagram */
.pu-ig-name{color:#000;font-weight:600;}
.pu-ig-sub{color:#737373;}
.pu-ig-ring{background:linear-gradient(45deg,#f9ce34,#ee2a7b,#6228d7);border-radius:999px;}
.pu-ig-av{background:#fff;color:#737373;border-radius:999px;}
.pu-ig-btn{background:#efefef;color:#000;border-radius:8px;font-weight:600;}
.pu-ig-bar{background:#efefef;border-radius:999px;overflow:hidden;}
.pu-ig-fill{background:#0095f6;border-radius:999px;}
`;
